import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

interface Role {
  id: string;
  code: string;
  name: string;
}

interface User {
  id: string;
  username: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  roles: Role[];
}

const INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 ' +
  'dark:bg-slate-800 dark:text-slate-100';

/**
 * 后台用户。
 *
 * ★ 在这一屏出现之前,**所有人共用一个 admin 账号** —— 后端的权限体系
 *   一直是完整的(每个接口都有权限校验),缺的只是建号的入口。
 *   共用账号的代价有两个,而且都只在出事时才付:
 *   审计日志里全是同一个人名,查不出是谁改的;人走了之后
 *   只能改那一个密码,等于所有人重新对一遍。
 *
 * ★ 这里只管**办公室账号**。司机在「司机」那一屏,他们还带车辆、
 *   服务区域这些 —— 混在一起的话几十个司机会把三五个后台账号淹掉。
 */
export function UserSettings({ canManage }: { canManage: boolean }) {
  const [rows, setRows] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [editing, setEditing] = useState<User | 'new' | null>(null);
  /** 刚建号或刚重置密码 —— 这个密码只显示这一次 */
  const [credential, setCredential] = useState<{ name: string; password: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([api<User[]>('/users'), api<Role[]>('/roles')]);
      setRows(u);
      setRoles(r);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function resetPassword(u: User) {
    if (!confirm(`重置 ${u.name} 的密码?他当前的登录会立即失效。`)) return;
    try {
      const r = await api<{ name: string; initialPassword: string }>(
        `/users/${u.id}/reset-password`,
        { method: 'POST' },
      );
      setCredential({ name: r.name, password: r.initialPassword });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '重置失败');
    }
  }

  async function toggleActive(u: User) {
    try {
      await api(`/users/${u.id}`, { method: 'PATCH', body: { isActive: !u.isActive } });
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '改不了');
    }
  }

  return (
    <section aria-label="用户">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">用户</h2>
          <p className="mt-1 text-sm text-slate-500">
            后台账号。每人一个 —— 共用一个账号的话,审计日志里全是同一个名字
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setEditing('new')}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white"
          >
            新建用户
          </button>
        )}
      </div>

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-800">
            <tr>
              <th className="p-3">姓名</th>
              <th className="p-3">登录名</th>
              <th className="p-3">角色</th>
              <th className="p-3">状态</th>
              <th className="p-3">最近登录</th>
              <th className="p-3 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 dark:border-slate-700/60">
                <td className="p-3 text-slate-900 dark:text-slate-100">{u.name}</td>
                <td className="p-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                  {u.username}
                </td>
                <td className="p-3 text-slate-600 dark:text-slate-400">
                  {u.roles.map((r) => r.name).join('、') || '—'}
                </td>
                <td className="p-3">
                  {u.isActive ? (
                    <span className="text-green-600">正常</span>
                  ) : (
                    <span className="text-slate-400">已停用</span>
                  )}
                  {/* ★ 还没改初始密码的要标出来 —— 那个密码是别人给他的 */}
                  {u.mustChangePassword && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700
                                     dark:bg-amber-900/40 dark:text-amber-300">
                      待改密
                    </span>
                  )}
                </td>
                <td className="p-3 text-xs text-slate-500">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '从没登录过'}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canManage && (
                    <>
                      <button
                        onClick={() => setEditing(u)}
                        className="text-brand-600 hover:underline dark:text-brand-500"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => void resetPassword(u)}
                        className="ml-3 text-brand-600 hover:underline dark:text-brand-500"
                      >
                        重置密码
                      </button>
                      <button
                        onClick={() => void toggleActive(u)}
                        className="ml-3 text-slate-500 hover:underline"
                      >
                        {u.isActive ? '停用' : '启用'}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <UserDialog
          existing={editing === 'new' ? undefined : editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSaved={async (c) => {
            setEditing(null);
            if (c) setCredential(c);
            await load();
          }}
        />
      )}

      {credential && (
        <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
      )}
    </section>
  );
}

function UserDialog({
  existing,
  roles,
  onClose,
  onSaved,
}: {
  existing?: User;
  roles: Role[];
  onClose: () => void;
  onSaved: (c?: { name: string; password: string }) => Promise<void>;
}) {
  const [f, setF] = useState({
    username: existing?.username ?? '',
    name: existing?.name ?? '',
    email: existing?.email ?? '',
    phone: existing?.phone ?? '',
    // 和建司机一样:给一个好念的默认值,他第一次登录必须改掉
    password: '123456',
  });
  const [roleIds, setRoleIds] = useState<string[]>(existing?.roles.map((r) => r.id) ?? []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      if (existing) {
        await api(`/users/${existing.id}`, {
          method: 'PATCH',
          body: {
            name: f.name,
            email: f.email || null,
            phone: f.phone || null,
            roleIds,
          },
        });
        await onSaved();
      } else {
        const r = await api<{ name: string; initialPassword: string }>('/users', {
          method: 'POST',
          body: {
            username: f.username.trim(),
            name: f.name,
            email: f.email || undefined,
            phone: f.phone || undefined,
            password: f.password.trim() || undefined,
            roleIds,
          },
        });
        await onSaved({ name: r.name, password: r.initialPassword });
      }
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="用户"
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5
                   dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {existing ? `编辑 ${existing.name}` : '新建用户'}
        </h3>

        <div className="space-y-3">
          {!existing && (
            <label className="block text-sm">
              <span className="text-slate-500">登录名</span>
              <span className="block text-xs text-slate-400">建好之后改不了</span>
              <input
                aria-label="登录名"
                value={f.username}
                onChange={(e) => setF({ ...f, username: e.target.value })}
                className={`${INPUT} font-mono`}
              />
            </label>
          )}

          <label className="block text-sm">
            <span className="text-slate-500">姓名</span>
            <input
              aria-label="姓名"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className={INPUT}
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="text-slate-500">邮箱(可不填)</span>
              <input
                aria-label="邮箱"
                value={f.email}
                onChange={(e) => setF({ ...f, email: e.target.value })}
                className={INPUT}
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-500">手机(可不填)</span>
              <input
                aria-label="手机"
                value={f.phone}
                onChange={(e) => setF({ ...f, phone: e.target.value })}
                className={INPUT}
              />
            </label>
          </div>

          {!existing && (
            <label className="block text-sm">
              <span className="text-slate-500">初始密码</span>
              <span className="block text-xs text-slate-400">
                默认 123456 —— 他第一次登录会强制改掉
              </span>
              <input
                aria-label="初始密码"
                value={f.password}
                onChange={(e) => setF({ ...f, password: e.target.value })}
                className={`${INPUT} font-mono`}
              />
            </label>
          )}

          {/*
            ★ 角色是**必填**。没有角色的账号能登录但什么都做不了 ——
              而那个人只会说「系统坏了」,没人会想到是没给角色。
          */}
          <div>
            <div className="mb-1 text-sm text-slate-500">角色(至少选一个)</div>
            <div className="space-y-1 rounded-lg border border-slate-200 p-2 dark:border-slate-700">
              {roles.map((r) => (
                <label key={r.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={roleIds.includes(r.id)}
                    onChange={(e) =>
                      setRoleIds((prev) =>
                        e.target.checked ? [...prev, r.id] : prev.filter((x) => x !== r.id),
                      )
                    }
                  />
                  <span className="text-slate-800 dark:text-slate-200">{r.name}</span>
                  <span className="font-mono text-xs text-slate-400">{r.code}</span>
                </label>
              ))}
            </div>
          </div>

          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-slate-500">
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !f.name || roleIds.length === 0 || (!existing && !f.username)}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 密码只显示这一次。
 *
 * ★ 库里存的是哈希 —— 关掉这个框就再也拿不回来,只能再重置一次。
 *   这句话必须写在框里,而不是指望人自己知道。
 */
function CredentialDialog({
  credential,
  onClose,
}: {
  credential: { name: string; password: string };
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="初始密码"
        className="w-full max-w-sm rounded-2xl bg-white p-5 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {credential.name} 的初始密码
        </h3>
        <p className="mb-3 text-sm text-slate-500">
          只显示这一次 —— 关掉就拿不回来了,只能再重置一次
        </p>
        <div className="rounded-lg bg-slate-100 px-3 py-3 text-center font-mono text-xl
                        text-slate-900 dark:bg-slate-800 dark:text-slate-100">
          {credential.password}
        </div>
        <p className="mt-3 text-xs text-slate-400">他第一次登录会被要求改成自己的密码</p>
        <button
          onClick={onClose}
          className="mt-4 w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white"
        >
          我记下了
        </button>
      </div>
    </div>
  );
}
